import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { Row, theme } from 'antd';
import {
  IGaps,
  IGapsInfo,
  ITextAreaBoxProps,
  IPopoverState
} from 'store/ducks/Gaps/types';
import { gapsOp } from 'store/ducks/Gaps';
import { RootState } from 'store/configureStore';
import Helpers from 'utils/Helpers';
import CustomTextarea from '../CustomTextarea';
import CustomIconButton from '../CustomIconButton';
import CustomCheckbox from '../CustomCheckbox';
import RichTextEditor from '../RichTextEditor';
import { stripHtmlTags } from 'utils/stripHtmlTags';
import useDeleteWithDependencyCheck from 'utils/hooks/useDeleteWithDependencyCheck';
import ApiService from 'utils/ApiCalls';
import * as Styled from './TextAreaBoxGap.styled';

const { useToken } = theme;

export const TextAreaBoxGap: React.FC<ITextAreaBoxProps> = ({
  placeholder,
  data,
  withCheckbox = false,
  refId,
  onChange,
  onDelete
}) => {
  const { token } = useToken();
  const { scheme } = useSelector((state: RootState) => state.global);

  const [dataObj, setDataObj] = useState<IGaps>({});
  const [gapNoticed, setGapNoticed] = useState(false);
  const [popoverState, setPopoverState] = useState<IPopoverState>({});

  const { handleDelete: handleGapDelete } = useDeleteWithDependencyCheck({
    tableName: 'gaps',
    softDeleteFn: (id) => ApiService.softDeleteRecord('gaps', id),
    onSuccess: (id) => {
      setDataObj((prevState: IGaps) => {
        const copy = { ...prevState };
        delete copy[id];
        return copy;
      });
      onDelete(id);
    },
    entityLabel: 'gap'
  });

  const { commentsData } = useSelector(
    (state: RootState) => state.complianceEst
  );

  useEffect(() => {
    let commentsState: IPopoverState = {};

    if (!Helpers.isObjEmpty(dataObj)) {
      Object.values(dataObj).forEach((item: IGapsInfo) => {
        if (item.gap_id) {
          commentsState[item.gap_id] = false;
        }
      });
    }

    setPopoverState(commentsState);
  }, [dataObj]);

  useEffect(() => {
    if (data && !Helpers.isObjEmpty(data)) {
      setDataObj(data);
      setGapNoticed(true);
    }
  }, [data]);

  const handleAddEmptyGap = (id: string) => {
    const newEntry = { gap_desc: '', gap_bru_ref: refId };
    setDataObj((prevState: IGaps) => ({
      ...prevState,
      [id]: newEntry
    }));
    onChange({ [id]: newEntry });
  };

  const handlePlusClick = () => {
    if (!gapNoticed) return;
    const newId = uuidv4();
    handleAddEmptyGap(newId);
  };

  const handleDeleteClick = (key: number | string) => {
    if (data[key]?.gap_id) {
      handleGapDelete(data[key].gap_id as number);
    } else {
      setDataObj((prevState: IGaps) => {
        const copy = { ...prevState };
        delete copy[key];
        return copy;
      });
      onDelete(key);
    }
  };

  const handleGapChange = (value: string, key: number) => {
    setDataObj((prevState: IGaps) => ({
      ...prevState,
      [key]: {
        gap_id: prevState[key].gap_id,
        gap_desc: value,
        gap_bru_ref: refId
      }
    }));

    onChange({
      [key]: { ...dataObj[key], gap_desc: value, gap_bru_ref: refId }
    });
  };

  const handleCheckboxChange = () => {
    setGapNoticed(!gapNoticed);
    if (!gapNoticed && Helpers.isObjEmpty(dataObj)) {
      handlePlusClick();
    }
  };

  // const drawComments = () => {
  //   const filteredComments = commentsData?.length
  //     ? commentsData.filter(
  //         (commentsDataItem: CommentItem) =>
  //           commentsDataItem.res_table_pk_ref === refId
  //       )
  //     : [];

  //   return (
  //     <>
  //       <CommentSection
  //         reqId={refId}
  //         data={filteredComments}
  //         tableRef={CommentTable.GAPS}
  //       />
  //       <a onClick={() => hide(refId)}>Close</a>
  //     </>
  //   );
  // };

  const hide = (id: number | string) => {
    if (!id) return;

    setPopoverState((prevState) => ({
      ...prevState,
      [id]: false
    }));
  };

  const handleOpenChange = (id: number | string) => {
    if (!id) return;

    setPopoverState((prevState) => ({
      ...prevState,
      [id]: !prevState[id]
    }));
  };

  useEffect(() => {
    if (dataObj && !Helpers.isObjEmpty(dataObj)) {
      setGapNoticed(true);
    }
  }, [dataObj]);

  return (
    <Styled.StyledContainer withCheckbox={withCheckbox}>
      <Row>
        <Styled.CheckboxContainer>
          {withCheckbox && (
            <CustomCheckbox
              disabled={false}
              checked={gapNoticed}
              onChange={handleCheckboxChange}
            />
          )}
        </Styled.CheckboxContainer>
        <Styled.TextAreaContainer withCheckbox={withCheckbox} scheme={scheme}>
          {dataObj &&
            Object.keys(dataObj).map((key: any, index: number) => (
              <Styled.TextAreaElementsContainer
                key={dataObj[key].gap_id || index}
              >
                <RichTextEditor
                  value={dataObj[key].gap_desc}
                  onChange={(value) => handleGapChange(value, key)}
                  onSave={(value) => handleGapChange(value, key)}
                  placeholder={placeholder}
                  scheme={scheme}
                />
                <Styled.StyledButtonSection token={token}>
                  <Styled.StyledCustomIconButton
                    token={token}
                    type='primary'
                    shape='circle'
                    icon={<DeleteOutlined />}
                    cursor='pointer'
                    size='small'
                    onClick={() => handleDeleteClick(key)}
                  />
                </Styled.StyledButtonSection>
              </Styled.TextAreaElementsContainer>
            ))}
          <Styled.ButtonContainer>
            <CustomIconButton
              type='primary'
              shape='circle'
              icon={<PlusOutlined />}
              size='middle'
              onClick={handlePlusClick}
              disabled={!gapNoticed}
            />
          </Styled.ButtonContainer>
        </Styled.TextAreaContainer>
      </Row>
      <Styled.FlexContainer />
    </Styled.StyledContainer>
  );
};
